import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import moment from "moment";
import classes from "../../styles/DashboardView.module.css";
import { WeekRevenueCompare } from "../../utilities/dashboardAggregations";
import { numberWithCommas } from "../Oversigt";

interface WeeklyRevenueTableProps {
    data: WeekRevenueCompare[];
    basisLabel: string;
    currentYearLabel: string;
    prevYearLabel: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function Delta({ value, isCurrency = false }: { value: number; isCurrency?: boolean }) {
    if (value === 0) return <span style={{ color: "#8a8f96" }}>–</span>;
    const color = value > 0 ? "#1b8a5a" : "#c53a31";
    const sign = value > 0 ? "+" : "−";
    const abs = Math.abs(value);
    return (
        <span style={{ color, fontWeight: 600 }}>
            {sign}{numberWithCommas(abs)}{isCurrency ? " kr" : ""}
        </span>
    );
}

export function WeeklyRevenueTable({ data, basisLabel, currentYearLabel, prevYearLabel }: WeeklyRevenueTableProps) {
    const totalCurrentRevenue = data.reduce((s, w) => s + w.currentRevenue, 0);
    const totalPrevRevenue = data.reduce((s, w) => s + w.previousRevenue, 0);
    const totalCurrentCount = data.reduce((s, w) => s + w.currentCount, 0);
    const totalPrevCount = data.reduce((s, w) => s + w.previousCount, 0);

    return (
        <Paper className={classes.chartCard} elevation={2}>
            <div className={classes.chartHeader}>
                <h3 className={classes.chartTitle}>
                    Ugentlig omsætning <span className={classes.chartSubtitle}>· {basisLabel}</span>
                </h3>
            </div>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell rowSpan={2}>Uge</StyledTableCell>
                            <StyledTableCell rowSpan={2}>Startdato</StyledTableCell>
                            <StyledTableCell rowSpan={2}>Slutdato</StyledTableCell>
                            <StyledTableCell align="center" colSpan={3}>Tilmeldinger</StyledTableCell>
                            <StyledTableCell align="center" colSpan={3}>Omsætning</StyledTableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell align="right">{currentYearLabel}</StyledTableCell>
                            <StyledTableCell align="right">{prevYearLabel}</StyledTableCell>
                            <StyledTableCell align="right">Δ</StyledTableCell>
                            <StyledTableCell align="right">{currentYearLabel}</StyledTableCell>
                            <StyledTableCell align="right">{prevYearLabel}</StyledTableCell>
                            <StyledTableCell align="right">Δ</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((w) => {
                            const deltaCount = w.currentCount - w.previousCount;
                            const deltaRevenue = w.currentRevenue - w.previousRevenue;
                            return (
                                <StyledTableRow key={`${w.weekNumber}-${w.startDate.toISOString()}`}>
                                    <StyledTableCell>{w.weekNumber}</StyledTableCell>
                                    <StyledTableCell>{moment(w.startDate).format("DD-MM-YY")}</StyledTableCell>
                                    <StyledTableCell>{moment(w.endDate).format("DD-MM-YY")}</StyledTableCell>
                                    <StyledTableCell align="right">{w.currentCount}</StyledTableCell>
                                    <StyledTableCell align="right">{w.previousCount}</StyledTableCell>
                                    <StyledTableCell align="right"><Delta value={deltaCount} /></StyledTableCell>
                                    <StyledTableCell align="right">{numberWithCommas(w.currentRevenue)} kr</StyledTableCell>
                                    <StyledTableCell align="right">{numberWithCommas(w.previousRevenue)} kr</StyledTableCell>
                                    <StyledTableCell align="right"><Delta value={deltaRevenue} isCurrency /></StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                        <StyledTableRow>
                            <StyledTableCell colSpan={3}><strong>Total</strong></StyledTableCell>
                            <StyledTableCell align="right"><strong>{totalCurrentCount}</strong></StyledTableCell>
                            <StyledTableCell align="right"><strong>{totalPrevCount}</strong></StyledTableCell>
                            <StyledTableCell align="right"><strong><Delta value={totalCurrentCount - totalPrevCount} /></strong></StyledTableCell>
                            <StyledTableCell align="right"><strong>{numberWithCommas(totalCurrentRevenue)} kr</strong></StyledTableCell>
                            <StyledTableCell align="right"><strong>{numberWithCommas(totalPrevRevenue)} kr</strong></StyledTableCell>
                            <StyledTableCell align="right"><strong><Delta value={totalCurrentRevenue - totalPrevRevenue} isCurrency /></strong></StyledTableCell>
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}
